import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";

import { API } from "../constant/api";
import useAuthAxios from "../utils/baseAxios";
import { errorMessage } from "../utils/errorMessage";
import type { UploadStatus } from "../pages/settings/components/DocumentUploadField";

/**
 * Two-step business image upload — ask the API for a signed URL, then PUT the
 * file straight to storage — for anything that goes through
 * `businesses/request-upload-url`: "LOGO" for the business logo, "PAGE" for
 * payment-page images.
 *
 * These land in the *public* bucket (unlike KYC documents in
 * `use-document-uploads`, which are private), so the returned `objectURL` can
 * be rendered directly with no signing.
 *
 * `initialURL` seeds an already-saved image so edit screens can show what is
 * currently live; `isDirty` then tells the caller whether the user actually
 * changed it, which is what decides if the field goes into a PATCH body.
 */
export const useBusinessImageUpload = ({
  documentType,
  initialURL,
  onUploaded,
}: {
  documentType: "LOGO" | "PAGE";
  initialURL?: string;
  /** Fires once the file is in storage — used by screens that auto-save. */
  onUploaded?: (objectURL: string) => void;
}) => {
  const axiosInstance = useAuthAxios();

  const [file, setFile] = useState<File | null>(null);
  const [objectURL, setObjectURL] = useState(initialURL ?? "");
  const [status, setStatus] = useState<UploadStatus>(
    initialURL ? "uploaded" : "idle"
  );
  const [error, setError] = useState<string | undefined>();
  // Local blob URL for instant preview while the PUT is in flight.
  const [previewURL, setPreviewURL] = useState("");

  // The URL the image had when it was last synced from the server — the
  // baseline `isDirty` compares against.
  const [savedURL, setSavedURL] = useState(initialURL ?? "");

  // `upload` is memoized, so calling `onUploaded` directly would freeze the
  // first render's closure — and on the settings tab that closure still sees
  // the business as loading (and therefore editable), which would skip the
  // auto-save. Read the latest callback off a ref instead.
  const onUploadedRef = useRef(onUploaded);
  useEffect(() => {
    onUploadedRef.current = onUploaded;
  });

  // Revoke the previous blob URL whenever it is replaced or the hook unmounts,
  // otherwise every re-pick leaks one for the lifetime of the document.
  const previewRef = useRef("");
  useEffect(() => {
    previewRef.current = previewURL;
  }, [previewURL]);
  useEffect(
    () => () => {
      if (previewRef.current) URL.revokeObjectURL(previewRef.current);
    },
    []
  );

  const replacePreview = useCallback((next: string) => {
    setPreviewURL((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return next;
    });
  }, []);

  /** Adopt a URL loaded from the server as the new clean baseline. */
  const sync = useCallback(
    (url?: string) => {
      const next = url ?? "";
      setSavedURL(next);
      setObjectURL(next);
      setFile(null);
      setError(undefined);
      setStatus(next ? "uploaded" : "idle");
      replacePreview("");
    },
    [replacePreview]
  );

  const upload = useCallback(
    async (selected: File) => {
      setFile(selected);
      setStatus("uploading");
      setError(undefined);
      replacePreview(URL.createObjectURL(selected));

      try {
        const { data } = await axiosInstance.post(
          API.requestBusinessImageUpload,
          { documentType }
        );

        // Plain axios, not the auth instance: the signed URL is pre-authorised
        // and S3 rejects the request if an Authorization header rides along.
        await axios.put(data.data.signedURL, selected, {
          headers: { "Content-Type": selected.type },
        });

        setObjectURL(data.data.objectURL as string);
        setStatus("uploaded");
        onUploadedRef.current?.(data.data.objectURL as string);
      } catch (e: any) {
        const message = errorMessage(
          e?.response?.data?.message || e?.message || "Upload failed"
        );
        setStatus("error");
        setError(message);
        toast.error(message);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [documentType, replacePreview]
  );

  const retry = useCallback(() => {
    if (file) upload(file);
  }, [file, upload]);

  const clear = useCallback(() => {
    setFile(null);
    setObjectURL("");
    setStatus("idle");
    setError(undefined);
    replacePreview("");
  }, [replacePreview]);

  return {
    file,
    /** What to render: the local preview while uploading, else the stored URL. */
    displayURL: previewURL || objectURL,
    /** The stored URL to send to the API. Empty until an upload succeeds. */
    objectURL,
    status,
    error,
    isUploading: status === "uploading",
    isUploaded: status === "uploaded" && !!objectURL,
    isDirty: objectURL !== savedURL,
    upload,
    retry,
    clear,
    sync,
  };
};
